import { createTheme, SxProps } from '@mui/material/styles';
import CSS from 'csstype';
import { fonts, predefinedFonts } from './fontStyles';
import type {} from '@mui/x-date-pickers/themeAugmentation';

export const DashboardColors = {
  title: '#6C6C6C',
  light_blue: '#1EE1CF',
  dark_yellow: '#DFDC1F'
};

export const Colors = {
  main: '#12497F',
  secondary: '#2274BC',
  third: '#69AAF3',
  gradient:
    'transparent linear-gradient(270deg, #2274BC 0%, #12497F 100%) 0% 0% no-repeat padding-box',
  white: '#FFFFFF',
  gray: '#C6C5C2',
  dark_gray: '#8B8B8B',
  skyBlue: '#0EAEFF',
  lightBlue: '#E3F5FE',
  modalBackground: '#12497FCC 0% 0% no-repeat padding-box',
  green: '#82CD2B',
  red: '#F44336',
  orange: '#FF5722',
  warning: '#EBA61C',
  pink: '#ED2AED',
  purple: '#5507BD',
  dark_green: '#00BBA8',
  box_shadow_color: '#69AAF34D',
  input_background: '#EDF2F7',
  input_field_background: '#E0E8F294'
};

const disabledProps = {
  '&:disabled': {
    background: Colors.gray,
    color: Colors.white,
    border: 'none'
  }
};

export const createMainButton = {
  ...predefinedFonts.semiBold14px22px,
  // width: '180px',
  height: '36px',
  // background: Colors.gradient,
  background: Colors.skyBlue,
  boxShadow: '0px 2px 10px #69AAF34D',
  zIndex: 1,
  opacity: '1',
  // borderRadius: '2px',
  borderRadius: '8px',
  '&:hover': {
    // background: Colors.gradient,
    background: Colors.skyBlue,
    boxShadow: '0px 2px 10px #69AAF34D',
    borderRadius: '8px'
  },
  ...disabledProps
};

export const createSecondaryButton = {
  ...predefinedFonts.Bold['12px17px'],
  width: '126px',
  height: '30px',
  border: '1px solid ' + Colors.secondary,
  // boxShadow: '0px 2px 10px #69AAF34D',
  color: Colors.secondary,
  zIndex: 1,
  opacity: '1',
  borderRadius: '2px',
  '&:hover': {
    boxShadow: '0px 2px 10px #69AAF34D',
    borderRadius: '2px'
  },
  ...disabledProps
};

export const editButton: CSS.Properties = {
  color: Colors.skyBlue + 'CC',
  textDecoration: 'underline'
};

const saveButtonProps: CSS.Properties = {
  background: Colors.secondary + ' 0% 0% no-repeat padding-box',
  opacity: '1',
  width: '125px',
  height: '36px',
  ...predefinedFonts.semiBold14px22px,
  color: Colors.white,
  marginLeft: '20px',
  textTransform: 'uppercase',
  borderRadius: '10px'
};

export const saveButton = {
  ...saveButtonProps,
  '&:hover': {
    ...saveButtonProps
  }
};

const cancelButtonProps: SxProps = {
  width: 'auto',
  minWidth: '125px',
  paddingX: '16px',
  height: '36px',
  border: '1px solid ' + Colors.secondary,
  ...predefinedFonts.semiBold14px22px,
  color: Colors.secondary,
  textTransform: 'uppercase',
  borderRadius: '10px'
};

export const cancelButton = {
  ...cancelButtonProps,
  '&:hover': {
    ...cancelButtonProps,
    background: Colors.secondary,
    color: Colors.white
  }
};

export const loginButton = {
  ...predefinedFonts.semiBold14px22px,
  background: Colors.gradient,
  boxShadow: 'none',
  color: Colors.white,
  textTransform: 'uppercase',
  height: '36px',
  '&:hover': {
    ...predefinedFonts.semiBold14px22px,
    border: 'solid 2px transparent',
    backgroundImage:
      'linear-gradient(rgba(255, 255, 255, 0), rgba(255, 255, 255, 0)), linear-gradient(101deg, ' +
      Colors.main +
      ' ,' +
      Colors.secondary +
      ')',
    backgroundOrigin: 'border-box',
    backgroundClip: 'content-box, border-box',
    boxShadow: '2px 1000px 1px #fff inset',
    color: Colors.main,
    height: '36px'
  }
};

export const createOverviewCreateButton = {
  ...predefinedFonts.semiBold14px22px,
  ...createMainButton,
  width: 'auto',
  margin: 0
  // marginLeft: '40px',
  // marginTop: '45px'
  // marginRight: '20px'
};

const selectedButtonProps: CSS.Properties = {
  ...fonts.Predefined.semiBold14px22px,
  textTransform: 'none',
  boxShadow: 'none',
  zIndex: 1,
  opacity: '1',
  width: 'auto',
  height: '36px',
  background: Colors.skyBlue,
  color: Colors.white,
  border: `1px solid ${Colors.skyBlue}`,
  marginLeft: '10px'
};

export const selectedButton = {
  ...selectedButtonProps,
  '&:hover': {
    boxShadow: 'none',
    background: Colors.white,
    color: Colors.skyBlue
  }
};

const deSelectedButtonProps: CSS.Properties = {
  ...fonts.Predefined.semiBold14px22px,
  textTransform: 'none',
  boxShadow: 'none',
  zIndex: 1,
  opacity: '1',
  width: 'auto',
  height: '36px',
  background: Colors.white,
  color: Colors.skyBlue,
  border: `1px solid ${Colors.skyBlue}`,
  marginLeft: '10px'
};

export const deSelectedButton = {
  ...deSelectedButtonProps,
  '&:hover': {
    background: Colors.skyBlue,
    color: Colors.white,
    boxShadow: 'none'
  }
};

export const buttons = {
  createMain: createMainButton,
  createSecondary: createSecondaryButton,
  edit: editButton,
  save: saveButton,
  cancel: cancelButton,
  loginButton: loginButton,
  overviewCreate: createOverviewCreateButton,
  selected: selectedButton,
  deSelected: deSelectedButton
};

export const shapes = {
  borderRadius: 8
};

export const buildTheme = () => {
  const theme = createTheme({
    palette: {
      primary: {
        main: Colors.main,
        light: Colors.secondary
      },
      error: {
        main: Colors.red
      }
    },
    shape: {
      borderRadius: shapes.borderRadius
    },
    typography: {
      button: {
        ...predefinedFonts.semiBold14px22px
      },
      h3: {
        ...predefinedFonts.regular14px16px
      }
    },
    components: {
      MuiDatePicker: {
        styleOverrides: {
          root: {
            backgroundColor: 'red'
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            boxSizing: 'border-box'
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            width: '125px',
            height: '36px',
            ...predefinedFonts.semiBold14px22px,
            textTransform: 'uppercase',
            borderRadius: `${shapes.borderRadius}px`
          },
          contained: {
            background: Colors.secondary + ' 0% 0% no-repeat padding-box',
            opacity: '1',
            color: Colors.white,
            marginLeft: '20px',
            boxShadow: 'none',
            '&:hover': {
              background: Colors.secondary + ' 0% 0% no-repeat padding-box',
              opacity: '1'
            }
          },
          outlined: {
            border: '1px solid ' + Colors.secondary,
            color: Colors.secondary
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 46,
            height: 24,
            padding: 0,
            display: 'flex',
            overflow: 'visible',
            marginLeft: 20,
            marginTop: -5
          },
          switchBase: {
            padding: 2,
            paddingTop: 3,
            transitionDuration: '300ms',
            color: Colors.gray,
            '&.Mui-disabled': {
              '& + .MuiSwitch-track': {
                backgroundColor: Colors.gray,
                borderColor: Colors.gray
              }
            },
            '&.Mui-checked': {
              transform: 'translateX(24px)',
              color: 'white',
              '&.Mui-disabled': { color: 'white' },
              '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: Colors.green,
                borderColor: Colors.green
              }
            }
          },
          thumb: {
            width: 18,
            height: 18,
            boxShadow: 'none'
          },
          track: {
            border: `1px solid ${Colors.gray}`,
            borderRadius: 22 / 2,
            opacity: 1,
            backgroundColor: 'white'
          },
          checked: {}
        }
      }
    }
  });

  return theme;
};
